import {
  getPrintMethod,
  printAuto,
  type ReceiptData,
} from '@/utils/printSettings';
import {
  receiptTemplateService,
  type ReceiptTemplateKind,
} from '@/services/receiptTemplate.service';
import { ORDER_PAYMENT_LABEL } from '../parts/constants';
import type { TenantOrder } from '@/mocks/orders';

/** Map TenantOrder → ReceiptData. Reused bởi list row action + detail page.
 *  Store info để rỗng — agent template hoặc browser fallback đều có default
 *  fallback "ERP Gold". Nếu cần fill store name/address, caller truyền vào
 *  `storeOverride`. */
export function orderToReceiptData(
  row: TenantOrder,
  storeOverride?: { storeName?: string; storeAddress?: string; storePhone?: string },
): ReceiptData {
  const items =
    row.items?.map((it) => ({
      name: it.product_name,
      sku: it.sku,
      qty: it.qty,
      price: it.price,
    })) ?? [];

  return {
    orderCode: row.order_number,
    storeName: storeOverride?.storeName ?? '',
    storeAddress: storeOverride?.storeAddress ?? '',
    storePhone: storeOverride?.storePhone ?? '',
    items,
    subtotal: row.subtotal ?? row.total_amount,
    discount: row.discount ?? 0,
    total: row.total_amount,
    paymentMethod:
      (row.payment_method && (ORDER_PAYMENT_LABEL[row.payment_method] ?? row.payment_method)) ??
      '',
    amountPaid: row.total_amount,
    cashier: row.cashier?.name ?? '-',
    date: row.created_at ?? new Date().toISOString(),
  } as ReceiptData;
}

/** Map TenantOrder → render data shape mà BE preview API expect (giống
 *  RenderData ở app/modules/receipt-template/providers/receipt-render.service.ts). */
function toRenderData(
  row: TenantOrder,
  storeOverride?: { storeName?: string; storeAddress?: string; storePhone?: string },
): Record<string, unknown> {
  return {
    store: {
      name: storeOverride?.storeName,
      address: storeOverride?.storeAddress,
      phone: storeOverride?.storePhone,
    },
    order: {
      code: row.order_number,
      date: row.created_at,
      cashier: row.cashier?.name,
      items: (row.items ?? []).map((it, i) => ({
        index: i + 1,
        name: it.product_name,
        sku: it.sku,
        qty: it.qty,
        price: it.price,
        line_total: it.qty * it.price,
      })),
      subtotal: row.subtotal ?? row.total_amount,
      discount: row.discount ?? 0,
      total: row.total_amount,
      payment_method: row.payment_method
        ? ORDER_PAYMENT_LABEL[row.payment_method] ?? row.payment_method
        : '',
      amount_paid: row.total_amount,
    },
  };
}

/** In template với 1 đơn — fetch default theo `kind`, render qua BE preview
 *  API, in theo setting localStorage:
 *  - method='browser' (default cho invoice/gold_guarantee) → iframe +
 *    window.print(), user chọn máy in trong dialog hệ thống.
 *  - method='agent' (default cho receipt/label) → printAuto với
 *    ESC/POS template (chỉ kind=receipt+label support; invoice/
 *    gold_guarantee fallback browser kèm warn).
 *
 *  Default `kind='receipt'`. Caller pass 'invoice' / 'gold_guarantee' / 'label'.
 */
export async function printOrderReceipt(
  row: TenantOrder,
  storeOverride?: { storeName?: string; storeAddress?: string; storePhone?: string },
  kind: ReceiptTemplateKind = 'receipt',
): Promise<{ ok: boolean; method: 'browser' | 'agent' }> {
  const method = getPrintMethod(kind);
  const data = toRenderData(row, storeOverride);

  // Path 1: agent (chỉ wire cho receipt + label qua printAuto). Invoice +
  // gold_guarantee chưa có agent path → fallback browser kèm console warn.
  if (method === 'agent' && (kind === 'receipt' || kind === 'label')) {
    if (kind === 'receipt') {
      const res = await printAuto({
        type: 'receipt',
        data: orderToReceiptData(row, storeOverride),
        width: 80,
        openDrawerAfter: true,
      });
      return { ok: res.ok, method: 'agent' };
    }
    // label-kind through agent — printAuto with type='label' yêu cầu
    // template + LabelData. Hiện không sẵn sàng ở Orders flow → fallback browser.
  }

  if (method === 'agent') {
    console.warn(
      `[print] kind=${kind} setting=agent chưa có agent path — fallback browser print dialog`,
    );
  }

  // Path 2: browser (default cho invoice/gold_guarantee + agent unsupported).
  try {
    const tpl = await receiptTemplateService.getDefault(kind);
    if (tpl) {
      const html = await receiptTemplateService.preview(tpl._id, data);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      document.body.appendChild(iframe);
      iframe.srcdoc = html;
      await new Promise((r) => {
        iframe.onload = () => r(null);
      });
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => iframe.remove(), 2000);
      return { ok: true, method: 'browser' };
    }
  } catch (e) {
    console.warn('Template print fail, fallback legacy:', e);
  }

  // Legacy fallback (template fetch fail) — chỉ áp dụng cho receipt.
  if (kind === 'receipt') {
    const res = await printAuto({
      type: 'receipt',
      data: orderToReceiptData(row, storeOverride),
      width: 80,
      openDrawerAfter: true,
    });
    return { ok: res.ok, method };
  }
  throw new Error(`Không có template default cho kind=${kind}`);
}
